import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'
import HomeMarketplaceBanner from 'src/shared/features/home/components/home-marketplace-banner'
import CurrentSearchbar from 'src/shared/features/users/components/marketplace/current-search-bar'
import { useMarketplaceSearch } from 'src/shared/features/users/components/marketplace/hooks/use-marketplace-search'
import PublicUserList from 'src/shared/features/users/components/marketplace/public-user-list'
import MarketplaceTitle from 'src/shared/features/users/components/marketplace/title'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import useActiveFilterStore from 'src/shared/features/users/stores/marketplace/active-filter'
import { useWindowSize } from 'src/shared/hooks/use-window-size'

import PublicLayout from '../../layouts/public-layout'

const Marketplace = () => {
  const { width } = useWindowSize()
  let { city, state, profession } = useParams()
  const { isLoading: isLoadingMapKey } = useMapApiKey()
  const { updateSubmittedFilters, setIsUsingExactLocation } = useActiveFilterStore(
    useShallow((state) => ({
      updateSubmittedFilters: state.updateSubmittedFilters,
      setIsUsingExactLocation: state.setIsUsingExactLocation,
    })),
  )
  const { initializeFromUrl } = useMarketplaceSearch()
  const fetchedLocation = useRef<boolean>(false)
  const searchBarRef = useRef<HTMLDivElement>(null)
  const [searchBarOutOfView, setSearchBarOutOfView] = useState<boolean>(false)

  if (profession) {
    profession = decodeURIComponent(profession)
  }
  if (city) {
    city = decodeURIComponent(city)
  }
  if (state) {
    state = decodeURIComponent(state)
  }

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setSearchBarOutOfView(!entry.isIntersecting)
      },
      { threshold: 1 },
    )
    if (searchBarRef.current) observer.observe(searchBarRef.current)
    return () => observer.disconnect()
  }, [setSearchBarOutOfView])

  useEffect(() => {
    if (width >= 768) {
      setSearchBarOutOfView(false)
    }
  }, [width])

  // Initialize from URL params or get geolocation
  useEffect(() => {
    if (isLoadingMapKey) return

    const hasRouteParams = profession || city || state

    if (hasRouteParams) {
      initializeFromUrl()
      updateSubmittedFilters({
        profession: profession as Profession,
        city,
        state,
      })
      fetchedLocation.current = true // Prevent geolocation if we have URL params
    } else if (navigator.geolocation && !fetchedLocation.current) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          updateSubmittedFilters({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setIsUsingExactLocation(true)
          fetchedLocation.current = true
        },
        (error) => {
          console.error('Error getting location:', error)
          setIsUsingExactLocation(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      )
    }
  }, [updateSubmittedFilters, setIsUsingExactLocation, initializeFromUrl, isLoadingMapKey, profession, city, state])

  // const onSearchProfessionals = () => {
  //   navigate(paths.marketplace.filters.path)
  //   if (isMobile) {
  //     setActiveDropdown('profession')
  //   }
  // }

  return (
    <PublicLayout>
      <HomeMarketplaceBanner />
      <div className="mx-auto flex min-h-screen max-w-[2400px] flex-col pb-[60px]">
        <div className="mt-8 flex flex-col px-5 md:px-8">
          <div ref={searchBarRef} className="flex flex-row items-center gap-2">
            {width >= 768 ? (
              <MarketplaceTitle />
            ) : (
              <div className="mb-8 flex w-full flex-col items-center gap-2">
                <p className="text-sm text-muted">Search results for</p>
                <CurrentSearchbar />
              </div>
            )}
          </div>
          <PublicUserList />
        </div>

        {/* {isMobile && (
          <div className="fixed inset-x-0 bottom-0 z-10 flex h-[100px] items-center justify-center bg-transparent">
            <Button
              variant="outline"
              className="rounded-full border-[0.5px] border-primary/25 bg-white px-5 py-3 shadow-lg md:px-8 md:py-4"
              onClick={onSearchProfessionals}
            >
              <Search className="mr-3 inline-block size-[24px] text-muted" />
              <p className="text-base font-semibold text-muted">Search for professional</p>
            </Button>
          </div>
        )} */}
      </div>
      {searchBarOutOfView && width < 768 && (
        <div className="fixed inset-x-0 bottom-0 z-10 flex h-[100px] items-center justify-center bg-transparent px-5">
          <CurrentSearchbar />
        </div>
      )}
    </PublicLayout>
  )
}

export default Marketplace
